import {ArrowLeft, ArrowRight, Check, Heart, Loader2} from 'lucide-react';
import {Card, CardContent} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {trpc} from '@common-web/lib/trpc';
import {formatCurrency} from '@common/lib/utils';
import {PlanConfiguration, PlanVariation, WizardState} from './types';
import {WIZARD_STEPS} from './utils';

function formatFeatureType(featureType: string): string {
  return featureType
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

function firstVariation(config: PlanConfiguration): PlanVariation | undefined {
  return config.variations[0];
}

export function StepProductTemplate({
  state,
  onUpdate,
  clientId,
  onBack,
  onNext,
}: {
  state: WizardState;
  onUpdate: (partial: Partial<WizardState>) => void;
  clientId: string;
  onBack: () => void;
  onNext: () => void;
}) {
  const catalogQuery = trpc.internal.groupPlan.tenantPlanCatalog.useQuery(
    {clientId},
    {enabled: !!clientId},
  );
  const planSource = catalogQuery.data?.planSource;
  const isVba = planSource === 'vba';

  const configurationsQuery = trpc.internal.groupPlan.configuration.list.useQuery(
    {clientId},
    {enabled: !!clientId && planSource === 'postgres'},
  );

  const configurations = configurationsQuery.data?.configurations ?? [];
  const vbaPlans = catalogQuery.data?.planSource === 'vba' ? catalogQuery.data.plans : [];

  const selectedIds = new Set(state.selectedPlanVariationIds);
  const selectedVbaIds = new Set(state.selectedVbaPlanIds);

  function toggleVariation(variationId: string) {
    const next = selectedIds.has(variationId)
      ? state.selectedPlanVariationIds.filter((id) => id !== variationId)
      : [...state.selectedPlanVariationIds, variationId];
    onUpdate({selectedPlanVariationIds: next});
  }

  function toggleVbaPlan(planId: string) {
    const next = selectedVbaIds.has(planId)
      ? state.selectedVbaPlanIds.filter((id) => id !== planId)
      : [...state.selectedVbaPlanIds, planId];
    onUpdate({selectedVbaPlanIds: next});
  }

  const isLoading = catalogQuery.isLoading || (planSource === 'postgres' && configurationsQuery.isLoading);

  return (
    <Card className='border-gray-200 shadow-sm'>
      <CardContent className='p-6'>
        <h3 className='text-lg font-semibold text-brand-navy mb-1'>{WIZARD_STEPS[2].label}</h3>
        <p className='text-sm text-muted-foreground mb-6'>
          {isVba
            ? 'Select plans from the VBA catalog to attach to this group. This is optional — plans can be assigned later.'
            : 'Select plan configurations to assign. This is optional — plans can be assigned later.'}
        </p>

        {!clientId ? (
          <p className='text-sm text-amber-700 bg-amber-50 rounded-md p-3'>
            No client context available. Plans cannot be loaded without a client.
          </p>
        ) : isLoading ? (
          <div className='flex justify-center py-8'>
            <Loader2 className='h-5 w-5 animate-spin text-gray-400' />
          </div>
        ) : isVba ? (
          vbaPlans.length === 0 ? (
            <p className='text-sm text-gray-500 text-center py-8'>
              No VBA plans found for this tenant.
            </p>
          ) : (
            <div className='space-y-2'>
              {vbaPlans.map((plan) => {
                const isSelected = selectedVbaIds.has(plan.planId);
                return (
                  <div
                    key={plan.planId}
                    className={`flex items-center justify-between bg-white border rounded-xl p-4 shadow-sm cursor-pointer transition-all ${
                      isSelected
                        ? 'border-emerald-300 ring-1 ring-emerald-200 bg-emerald-50/30'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => toggleVbaPlan(plan.planId)}
                  >
                    <div>
                      <h3 className='font-semibold text-gray-900'>{plan.description}</h3>
                      <div className='flex items-center gap-2 mt-1'>
                        <Badge variant='secondary' className='text-xs font-mono font-normal'>
                          {plan.planId}
                        </Badge>
                        {plan.planType && (
                          <Badge variant='outline' className='text-xs text-gray-500'>
                            {plan.planType}
                          </Badge>
                        )}
                      </div>
                    </div>
                    {isSelected ? (
                      <Badge className='bg-emerald-100 text-emerald-700 text-xs shrink-0'>
                        <Check className='h-3 w-3 mr-1' />
                        Selected
                      </Badge>
                    ) : (
                      <Badge variant='outline' className='text-xs text-gray-500 shrink-0'>
                        Click to add
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )
        ) : configurations.length === 0 ? (
          <p className='text-sm text-gray-500 text-center py-8'>
            No plan configurations found for this client.
          </p>
        ) : (
          <div className='space-y-4'>
            {configurations.map((config) => {
              const variation = firstVariation(config);
              if (!variation) return null;
              const isSelected = selectedIds.has(variation.id);
              const features = variation.vbaFeatureConfigurationPriceVariations ?? [];

              return (
                <div
                  key={config.id}
                  className={`bg-white border rounded-xl overflow-hidden shadow-sm cursor-pointer transition-all ${
                    isSelected
                      ? 'border-emerald-300 ring-1 ring-emerald-200'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => toggleVariation(variation.id)}
                >
                  {/* Header */}
                  <div
                    className={`p-4 border-b ${isSelected ? 'border-emerald-100 bg-emerald-50/30' : 'border-gray-100'}`}
                  >
                    <div className='flex items-start justify-between'>
                      <div>
                        <h3 className='font-semibold text-gray-900'>{config.name}</h3>
                        <div className='flex items-center gap-2 mt-1'>
                          {variation.name && (
                            <Badge variant='secondary' className='text-xs font-normal'>
                              {variation.name}
                            </Badge>
                          )}
                          {config.includesFamilyCoverage && (
                            <Badge className='bg-rose-50 text-rose-700 border-rose-200 text-xs'>
                              <Heart className='h-3 w-3 mr-1' />
                              Family
                            </Badge>
                          )}
                          <span className='text-sm font-medium text-gray-900'>
                            {formatCurrency(variation.maxPayoutPEPM)}
                            <span className='font-normal text-gray-500'>/mo</span>
                            {(variation.maxPayoutPEPMES ||
                              variation.maxPayoutPEPMEC ||
                              variation.maxPayoutPEPMFA) && (
                              <span className='ml-1 text-xs text-gray-400'>
                                {variation.maxPayoutPEPMES &&
                                  `ES:${formatCurrency(variation.maxPayoutPEPMES)}`}
                                {variation.maxPayoutPEPMEC &&
                                  ` EC:${formatCurrency(variation.maxPayoutPEPMEC)}`}
                                {variation.maxPayoutPEPMFA &&
                                  ` FA:${formatCurrency(variation.maxPayoutPEPMFA)}`}
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                      {isSelected ? (
                        <Badge className='bg-emerald-100 text-emerald-700 text-xs shrink-0'>
                          <Check className='h-3 w-3 mr-1' />
                          Selected
                        </Badge>
                      ) : (
                        <Badge variant='outline' className='text-xs text-gray-500 shrink-0'>
                          Click to add
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Content — Features */}
                  {features.length > 0 && (
                    <div className='p-4'>
                      <div className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-2'>
                        Features
                      </div>
                      <div className='space-y-1'>
                        {features.map((pv) => {
                          const fc = pv.vbaFeatureConfiguration;
                          const featureLabel =
                            fc?.featureName ||
                            (fc?.featureType ? formatFeatureType(fc.featureType) : null) ||
                            pv.priceName;
                          return (
                            <div
                              key={pv.id}
                              className='flex items-center justify-between py-1.5 px-2 -mx-2 rounded'
                            >
                              <span className='text-sm text-gray-700'>{featureLabel}</span>
                              {'employeeOnlyPEPM' in pv && pv.employeeOnlyPEPM != null && (
                                <span className='text-sm text-gray-500 font-medium'>
                                  {formatCurrency(pv.employeeOnlyPEPM)}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className='flex items-center justify-between mt-6 pt-4 border-t'>
          <Button variant='outline' onClick={onBack}>
            <ArrowLeft className='h-4 w-4 mr-2' />
            Back
          </Button>
          <Button onClick={onNext}>
            Continue
            <ArrowRight className='h-4 w-4 ml-2' />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
